import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';

export function useWarehouses() {
  return useQuery({
    queryKey: ['catalog', 'warehouses'],
    queryFn: async () => {
      const { data } = await api.get('/catalog/warehouses');
      return data?.items || data?.warehouses || data?.data || [];
    },
    staleTime: 5 * 60 * 1000
  });
}

export function useCategories(warehouseId) {
  return useQuery({
    queryKey: ['catalog', 'categories', warehouseId || 'all'],
    enabled: !!warehouseId,
    queryFn: async () => {
      const { data } = await api.get('/catalog/categories', {
        params: { warehouseId }
      });
      return data?.items || data?.data || [];
    },
    staleTime: 60_000
  });
}

export function useAllCategories(warehouses = []) {
  const ids = warehouses.map((w) => w._id || w.id).filter(Boolean);
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['catalog', 'categories', id],
      queryFn: async () => {
        const { data } = await api.get('/catalog/categories', { params: { warehouseId: id } });
        return data?.items || data?.data || [];
      },
      staleTime: 60_000
    }))
  });
  const items = queries.flatMap((q) => q.data || []);
  const isLoading = queries.some((q) => q.isLoading);
  return { items, isLoading };
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/catalog/warehouses', body).then((r) => r.data),
    successMessage: 'تم إنشاء المخزن',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'warehouses'] })
  });
}

export function useUpdateWarehouse() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => api.patch(`/catalog/warehouses/${id}`, body).then((r) => r.data),
    successMessage: 'تم حفظ المخزن',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'warehouses'] })
  });
}

export function useDeleteWarehouse() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.delete(`/catalog/warehouses/${id}`).then((r) => r.data),
    successMessage: 'تم حذف المخزن',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'warehouses'] })
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/catalog/categories', body).then((r) => r.data),
    successMessage: 'تم إنشاء القسم',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] })
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => api.patch(`/catalog/categories/${id}`, body).then((r) => r.data),
    successMessage: 'تم حفظ التعديلات',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] })
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.delete(`/catalog/categories/${id}`).then((r) => r.data),
    successMessage: 'تم حذف القسم',
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] })
  });
}
